import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession } from '../../adminSession';
import '../../Admin.scss';

type AppStatus = 'pending_build' | 'in_progress' | 'connected' | 'suspended';

type AppRow = {
  id: number;
  status: AppStatus;
  buildVersion: string | null;
  note: string;
  updatedAt: string;
  firebaseKeyName?: string | null;
  firebaseProjectId?: string | null;
  privacyPolicyName?: string | null;
  // 앱 버전 관리 — buildVersion이 최신 배포 버전, minVersion 미만은 앱에서 강제 업데이트 안내
  minVersion?: string | null;
  iosStoreUrl?: string | null;
  androidStoreUrl?: string | null;
  updateMessage?: string | null;
  lastReportedVersion?: string | null;
  lastReportedAt?: string | null;
  // 선택 앱(관리자·교적부·재정·주일학교) 사용 여부. 기본 제공 앱(사용자앱)은 항상 1.
  enabled?: number;
} | null;

type ChurchAppRow = {
  authChurch: string;
  churchName: string;
  contactName: string | null;
  contactEmail: string | null;
  // 이 교회 데이터가 저장되는 area(서버 area DB·업로드 폴더). 앱 빌드의 tenantConfig.ts APP_AREA와 같아야 한다.
  area: number;
  // 결제로 등록된 교회의 관리자 로그인 정보 전달 상태(수동 등록 교회는 null)
  credentialStatus: CredentialStatus | null;
  // 계약 최종 결제 완료 여부 - 로그인 정보는 이 뒤에만 고객에게 전달할 수 있다.
  finalPaid: boolean;
  createdAt: string;
  apps: Record<AppTypeKey, AppRow>;
};

type CredentialStatus = 'pending' | 'sent' | 'account_failed';

type CredentialInfo = {
  exists: boolean;
  userAccount: string | null;
  credential: {
    status: CredentialStatus;
    tempPassword: string | null;
    sentAt: string | null;
    contactEmail: string | null;
    finalPaid: boolean;
  } | null;
};

const CREDENTIAL_LABEL: Record<CredentialStatus, string> = {
  pending: '로그인 정보 전달 전',
  sent: '로그인 정보 전달됨',
  account_failed: '관리자 계정 생성 실패',
};

const CREDENTIAL_TAG_MODIFIER: Record<CredentialStatus, string> = {
  pending: ' admin-status-tag--pending',
  sent: ' admin-status-tag--active',
  account_failed: ' admin-status-tag--danger',
};

type AppTypeKey = 'user' | 'admin' | 'roll' | 'finance' | 'sundayschool';
// 'user'(사용자앱)는 "사용 시작"만 있다(중지 없음). 수동 등록 교회는 앱 행이 하나도 없이 시작한다.
type OptionalAppType = 'user' | 'admin' | 'roll' | 'finance' | 'sundayschool';

// 결제 등록 교회는 사용자앱 + 신청서에서 고른 앱이 자동으로 켜지고, 수동 등록 교회는 여기서 앱마다 "사용 시작"한다.
// 선택 앱(관리자앱·교적부앱·재정앱·주일학교앱)은 사용 중지도 할 수 있다.
function isEnabledRow(row: AppRow): boolean {
  return Boolean(row) && Number(row?.enabled ?? 1) === 1;
}

const APP_TYPE_LABEL: Record<AppTypeKey, string> = {
  user: '사용자앱',
  admin: '관리자앱',
  roll: '교적부앱',
  finance: '재정앱',
  sundayschool: '주일학교앱',
};

type ApiEnvelope<T> = { ok: boolean; data?: T; message?: string };

type AreaOption = { area: number; name: string; isDefault: boolean };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

const STATUS_LABEL: Record<AppStatus, string> = {
  pending_build: '빌드 대기',
  in_progress: '빌드/심사 중',
  connected: '연동 완료',
  suspended: '중단',
};

const STATUS_TAG_MODIFIER: Record<AppStatus, string> = {
  pending_build: '',
  in_progress: ' admin-status-tag--pending',
  connected: ' admin-status-tag--active',
  suspended: ' admin-status-tag--danger',
};

type Draft = {
  status: AppStatus;
  buildVersion: string;
  note: string;
  minVersion: string;
  iosStoreUrl: string;
  androidStoreUrl: string;
  updateMessage: string;
};

function errorMessageOf(err: unknown, fallback: string): string {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message?: string }).message) || fallback;
  }
  return fallback;
}

function draftOf(row: AppRow): Draft {
  return {
    status: row?.status ?? 'pending_build',
    buildVersion: row?.buildVersion ?? '',
    note: row?.note ?? '',
    minVersion: row?.minVersion ?? '',
    iosStoreUrl: row?.iosStoreUrl ?? '',
    androidStoreUrl: row?.androidStoreUrl ?? '',
    updateMessage: row?.updateMessage ?? '',
  };
}

export default function AppManagementPage() {
  const session = getAdminSession();
  const [items, setItems] = useState<ChurchAppRow[]>([]);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    authChurch: '',
    churchName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    userAccount: '',
    password: '',
    area: '',
  });
  const [areaOptions, setAreaOptions] = useState<AreaOption[]>([]);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');

  const [appEditTarget, setAppEditTarget] = useState<{ authChurch: string; churchName: string } | null>(null);
  const [credentialInfo, setCredentialInfo] = useState<CredentialInfo | null>(null);
  const [credentialBusy, setCredentialBusy] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<{ authChurch: string; churchName: string } | null>(null);
  const [deleteTyped, setDeleteTyped] = useState('');

  const fetchItems = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get<ApiEnvelope<ChurchAppRow[]>>(`${MainURL}/admin-console/church-apps`, {
        params: { requesterId: session.id },
      });
      if (res.data?.ok && res.data.data) {
        // 옛 서버는 연동 전 주일학교앱을 id 없는 { status } 객체로 내려준다 - 실제 church_apps 행(id)만 행으로 취급.
        const churches = res.data.data.map((church) => {
          const ss = church.apps.sundayschool as AppRow | { id?: number } | null;
          return ss && !ss.id ? { ...church, apps: { ...church.apps, sundayschool: null } } : church;
        });
        setItems(churches);
        const nextDrafts: Record<number, Draft> = {};
        churches.forEach((church) => {
          if (church.apps.user) nextDrafts[church.apps.user.id] = draftOf(church.apps.user);
          if (church.apps.admin) nextDrafts[church.apps.admin.id] = draftOf(church.apps.admin);
          if (church.apps.roll) nextDrafts[church.apps.roll.id] = draftOf(church.apps.roll);
          if (church.apps.finance) nextDrafts[church.apps.finance.id] = draftOf(church.apps.finance);
          if (church.apps.sundayschool) nextDrafts[church.apps.sundayschool.id] = draftOf(church.apps.sundayschool);
        });
        setDrafts(nextDrafts);
      } else {
        setErrorMsg(res.data?.message || '앱 연동 현황을 불러오지 못했습니다.');
      }
    } catch (err) {
      setErrorMsg(errorMessageOf(err, '앱 연동 현황을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const updateDraft = (id: number, patch: Partial<Draft>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...draftOf(null), ...prev[id], ...patch } }));
  };

  const handleUploadFirebaseKey = async (id: number, file: File | undefined) => {
    if (!session || !file) return;
    const formData = new FormData();
    formData.append('requesterId', String(session.id));
    formData.append('keyFile', file);
    setSavingKey(`firebase-${id}`);
    try {
      const res = await axios.post<ApiEnvelope<null>>(`${MainURL}/admin-console/church-apps/${id}/firebase-key`, formData);
      if (!res.data?.ok) throw new Error(res.data?.message);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, 'Firebase 키를 업로드하지 못했습니다.'));
    } finally {
      setSavingKey(null);
    }
  };

  const handleDeleteFirebaseKey = async (id: number) => {
    if (!session) return;
    if (!window.confirm('이 앱의 Firebase 키를 서버에서 삭제합니다. 삭제하면 이 앱의 푸시 발송이 중단됩니다. 계속할까요?')) return;
    setSavingKey(`firebase-${id}`);
    try {
      const res = await axios.delete<ApiEnvelope<null>>(`${MainURL}/admin-console/church-apps/${id}/firebase-key`, {
        data: { requesterId: session.id },
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, 'Firebase 키를 삭제하지 못했습니다.'));
    } finally {
      setSavingKey(null);
    }
  };

  const handleUploadPrivacyPolicy = async (id: number, file: File | undefined) => {
    if (!session || !file) return;
    const formData = new FormData();
    formData.append('requesterId', String(session.id));
    formData.append('policyFile', file);
    setSavingKey(`policy-${id}`);
    try {
      const res = await axios.post<ApiEnvelope<null>>(`${MainURL}/admin-console/church-apps/${id}/privacy-policy`, formData);
      if (!res.data?.ok) throw new Error(res.data?.message);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, '개인정보처리방침을 업로드하지 못했습니다.'));
    } finally {
      setSavingKey(null);
    }
  };

  const handleDeletePrivacyPolicy = async (id: number) => {
    if (!session) return;
    if (!window.confirm('이 앱의 개인정보처리방침 파일을 서버에서 삭제합니다. 공개 URL도 함께 중단됩니다. 계속할까요?')) return;
    setSavingKey(`policy-${id}`);
    try {
      const res = await axios.delete<ApiEnvelope<null>>(`${MainURL}/admin-console/church-apps/${id}/privacy-policy`, {
        data: { requesterId: session.id },
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, '개인정보처리방침을 삭제하지 못했습니다.'));
    } finally {
      setSavingKey(null);
    }
  };

  const handleSaveApp = async (id: number) => {
    if (!session) return;
    const draft = drafts[id];
    if (!draft) return;
    setSavingKey(`app-${id}`);
    try {
      const res = await axios.put<ApiEnvelope<null>>(`${MainURL}/admin-console/church-apps/${id}`, {
        requesterId: session.id,
        ...draft,
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, '저장하지 못했습니다.'));
    } finally {
      setSavingKey(null);
    }
  };

  const fetchCredentialInfo = (authChurch: string) => {
    if (!session) return;
    axios
      .get<ApiEnvelope<CredentialInfo>>(
        `${MainURL}/admin-console/church-apps/${encodeURIComponent(authChurch)}/admin-account`,
        { params: { requesterId: session.id } },
      )
      .then((res) => {
        if (res.data?.ok && res.data.data) setCredentialInfo(res.data.data);
      })
      .catch(() => setCredentialInfo(null));
  };

  const openAppEditModal = (authChurch: string, churchName: string) => {
    setAppEditTarget({ authChurch, churchName });
    setCredentialInfo(null);
    fetchCredentialInfo(authChurch);
  };

  // 로그인 정보는 운영진이 고객에게 직접 알려 주고, 여기서 전달 완료만 표시한다(메일 발송 없음).
  const handleCredentialSent = async (authChurch: string) => {
    if (!session) return;
    if (
      !window.confirm(
        '고객에게 로그인 정보를 알려 준 것으로 표시합니다. 표시하면 이 화면에서 임시 비밀번호를 더 이상 볼 수 없습니다. 계속할까요?',
      )
    )
      return;
    setCredentialBusy(true);
    try {
      const res = await axios.post<ApiEnvelope<null>>(
        `${MainURL}/admin-console/church-apps/${encodeURIComponent(authChurch)}/credentials/mark-sent`,
        { requesterId: session.id },
      );
      if (!res.data?.ok) throw new Error(res.data?.message);
      fetchCredentialInfo(authChurch);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, '전달 상태를 바꾸지 못했습니다.'));
    } finally {
      setCredentialBusy(false);
    }
  };

  const renderCredentialSection = (church: ChurchAppRow) => {
    const info = credentialInfo;
    const credential = info?.credential ?? null;
    return (
      <div className="am-version-box" style={{ marginBottom: 16 }}>
        <div className="am-version-box__title">관리자 로그인 정보</div>
        {!info ? (
          <p className="am-version-box__desc">불러오는 중...</p>
        ) : !info.exists ? (
          <p className="am-version-box__desc">
            교회 관리자 계정이 없습니다. 교회관리에서 로그인 아이디와 비밀번호를 정해 계정을 만들어 주세요
            {credential?.status === 'account_failed' ? '(신청서의 관리자 이메일이 이미 다른 계정에 쓰이고 있어 자동 생성에 실패했습니다)' : ''}.
          </p>
        ) : (
          <>
            <div style={{ fontSize: 14, lineHeight: 1.8, userSelect: 'text' }}>
              <div>
                로그인 아이디: <strong style={{ userSelect: 'all' }}>{info.userAccount}</strong>
              </div>
              {credential?.status === 'pending' && credential.tempPassword && (
                <div>
                  임시 비밀번호: <strong style={{ userSelect: 'all', fontFamily: 'monospace' }}>{credential.tempPassword}</strong>
                </div>
              )}
              {credential && (
                <div>
                  상태:{' '}
                  <span className={`admin-status-tag${CREDENTIAL_TAG_MODIFIER[credential.status]}`}>
                    {CREDENTIAL_LABEL[credential.status]}
                  </span>
                  {credential.status === 'sent' && credential.sentAt
                    ? ` (${String(credential.sentAt).slice(0, 16).replace('T', ' ')})`
                    : ''}
                </div>
              )}
            </div>
            {!credential ? (
              <p className="am-version-box__desc" style={{ marginTop: 8 }}>
                직접 등록한 교회라 등록 때 입력한 비밀번호를 그대로 씁니다.
              </p>
            ) : credential.status === 'pending' ? (
              <>
                <p className="am-version-box__desc" style={{ marginTop: 8 }}>
                  이 계정으로 앱을 검수한 뒤, 계약 최종 결제가 끝나면 고객에게 아이디와 임시 비밀번호를 직접 알려 주고
                  &quot;전달 완료로 표시&quot;를 눌러 주세요.
                  {!credential.finalPaid && ' 아직 최종 결제 전이라 전달 완료로 표시할 수 없습니다.'}
                </p>
                <div className="am-actions" style={{ marginTop: 4 }}>
                  <button
                    type="button"
                    className="am-btn am-btn--primary"
                    onClick={() => handleCredentialSent(church.authChurch)}
                    disabled={credentialBusy || !credential.finalPaid}
                  >
                    {credentialBusy ? '처리 중...' : '전달 완료로 표시'}
                  </button>
                </div>
              </>
            ) : credential.status === 'sent' ? (
              <p className="am-version-box__desc" style={{ marginTop: 8 }}>
                고객에게 전달해 임시 비밀번호는 보관하지 않습니다. 다시 알려 줘야 하면 교회관리에서 비밀번호를 새로 정해 주세요.
              </p>
            ) : null}
          </>
        )}
      </div>
    );
  };

  const closeAppEditModal = () => {
    setAppEditTarget(null);
    setCredentialInfo(null);
  };

  const openCreateModal = () => {
    setCreateForm({
      authChurch: '',
      churchName: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      userAccount: '',
      password: '',
      area: '',
    });
    setCreateError('');
    setCreateOpen(true);
    if (session) {
      axios
        .get<ApiEnvelope<AreaOption[]>>(`${MainURL}/admin-console/areas`, { params: { requesterId: session.id } })
        .then((res) => {
          if (res.data?.ok && res.data.data) setAreaOptions(res.data.data);
        })
        .catch(() => setAreaOptions([]));
    }
  };

  const closeCreateModal = () => setCreateOpen(false);

  const submitCreate = async () => {
    if (!session) return;
    if (!createForm.authChurch.trim()) {
      setCreateError('authChurch(번들 ID)를 입력해 주세요.');
      return;
    }
    if (!createForm.churchName.trim()) {
      setCreateError('교회명을 입력해 주세요.');
      return;
    }
    if (!createForm.userAccount.trim() || !createForm.password) {
      setCreateError('로그인 아이디와 비밀번호를 입력해 주세요.');
      return;
    }
    if (!isValidEmail(createForm.userAccount)) {
      setCreateError('로그인 아이디는 이메일 형식으로 입력해 주세요.');
      return;
    }
    setCreateSubmitting(true);
    setCreateError('');
    try {
      const res = await axios.post<ApiEnvelope<{ authChurch: string }>>(`${MainURL}/admin-console/church-apps`, {
        requesterId: session.id,
        ...createForm,
        area: createForm.area ? Number(createForm.area) : undefined,
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      closeCreateModal();
      fetchItems();
    } catch (err) {
      setCreateError(errorMessageOf(err, '교회 등록에 실패했습니다.'));
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleToggleOptional = async (
    authChurch: string,
    churchName: string,
    appType: OptionalAppType,
    enabled: boolean,
  ) => {
    if (!session) return;
    const label = APP_TYPE_LABEL[appType];
    const blocked =
      appType === 'finance' ? `${label}과 웹 재정관리` : appType === 'sundayschool' ? `${label}과 웹 주일학교 관리` : label;
    const webNote =
      appType === 'admin' ? ' 웹 교회앱관리는 기본 제공이라 계속 쓸 수 있습니다.' : appType === 'roll' ? ' 웹 교적관리는 기본 제공이라 계속 쓸 수 있습니다.' : '';
    const startNote =
      appType === 'sundayschool'
        ? '\n주일학교 기본 정보(부서 \'주일학교\'와 기본 반)가 없으면 자동으로 만들고, 교회 관리자 계정으로 주일학교앱·웹 주일학교 관리에 로그인할 수 있습니다.'
        : '';
    const message = enabled
      ? `${churchName}에서 ${label}을 사용하도록 켭니다.${startNote} 계속할까요?`
      : `${churchName}의 ${label} 사용을 중지합니다.\n중지하면 이 교회는 ${blocked}에 로그인할 수 없습니다(데이터·설정은 지워지지 않고, 다시 켜면 그대로 이어서 쓸 수 있습니다).${webNote} 계속할까요?`;
    if (!window.confirm(message)) return;
    setSavingKey(`optional-${appType}-${authChurch}`);
    try {
      const res = await axios.put<ApiEnvelope<null>>(
        `${MainURL}/admin-console/church-apps/${encodeURIComponent(authChurch)}/optional-apps/${appType}`,
        { requesterId: session.id, enabled },
      );
      if (!res.data?.ok) throw new Error(res.data?.message);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, '사용 여부를 변경하지 못했습니다.'));
    } finally {
      setSavingKey(null);
    }
  };

  // 삭제 확인은 브라우저 기본 prompt 대신 자체 모달 - 안내 문구의 authChurch를 드래그해 복사할 수 있게.
  const openDeleteModal = (authChurch: string, churchName: string) => {
    setDeleteTarget({ authChurch, churchName });
    setDeleteTyped('');
  };

  const closeDeleteModal = () => setDeleteTarget(null);

  const handleDelete = async () => {
    if (!session || !deleteTarget) return;
    const { authChurch } = deleteTarget;
    if (deleteTyped !== authChurch) return;
    closeDeleteModal();
    setSavingKey(`delete-${authChurch}`);
    try {
      const res = await axios.delete<ApiEnvelope<null>>(`${MainURL}/admin-console/church-apps/${encodeURIComponent(authChurch)}`, {
        data: { requesterId: session.id },
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, '삭제에 실패했습니다.'));
    } finally {
      setSavingKey(null);
    }
  };

  // 앱 상세 수정 모달 안에서 사용자앱/관리자앱/주일학교앱 세 섹션이 공유하는 편집 UI
  // (빌드 상태/버전/메모 + Firebase 키 + 개인정보처리방침).
  const renderAppEditSection = (row: NonNullable<AppRow>, authChurch: string, appType: AppTypeKey) => {
    const draft = drafts[row.id] || draftOf(row);
    return (
      <div>
        <label>
          <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>빌드/연동 상태</span>
          <select value={draft.status} onChange={(e) => updateDraft(row.id, { status: e.target.value as AppStatus })}>
            {Object.entries(STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <div className="am-version-box">
          <div className="am-version-box__title">버전 관리</div>
          <p className="am-version-box__desc">
            앱은 시작할 때 자기 버전(tenantConfig.ts의 APP_VERSION)을 서버에 보내 확인합니다. 최소 지원 버전보다 낮으면
            닫을 수 없는 업데이트 안내가, 최신 배포 버전보다 낮으면 닫을 수 있는 안내가 뜹니다. 비워 두면 안내하지 않습니다.
          </p>
          <div className="am-version-box__grid">
            <label>
              <span>최신 배포 버전</span>
              <input
                type="text"
                placeholder="예: 1.2.0"
                value={draft.buildVersion}
                onChange={(e) => updateDraft(row.id, { buildVersion: e.target.value })}
              />
            </label>
            <label>
              <span>최소 지원 버전 (미만은 강제 업데이트)</span>
              <input
                type="text"
                placeholder="예: 1.1.0"
                value={draft.minVersion}
                onChange={(e) => updateDraft(row.id, { minVersion: e.target.value })}
              />
            </label>
            <label>
              <span>App Store 주소</span>
              <input
                type="url"
                placeholder="https://apps.apple.com/app/..."
                value={draft.iosStoreUrl}
                onChange={(e) => updateDraft(row.id, { iosStoreUrl: e.target.value })}
              />
            </label>
            <label>
              <span>Play 스토어 주소</span>
              <input
                type="url"
                placeholder="https://play.google.com/store/apps/details?id=..."
                value={draft.androidStoreUrl}
                onChange={(e) => updateDraft(row.id, { androidStoreUrl: e.target.value })}
              />
            </label>
          </div>
          <label>
            <span>업데이트 안내 문구 (비우면 기본 문구)</span>
            <textarea
              rows={2}
              placeholder="새 기능이 추가되었어요. 업데이트 후 이용해 주세요."
              value={draft.updateMessage}
              onChange={(e) => updateDraft(row.id, { updateMessage: e.target.value })}
            />
          </label>
          <div className="am-version-box__reported">
            앱에서 마지막으로 확인된 버전:{' '}
            {row.lastReportedVersion ? (
              <strong>
                {row.lastReportedVersion}
                {row.lastReportedAt ? ` (${String(row.lastReportedAt).slice(0, 16).replace('T', ' ')})` : ''}
              </strong>
            ) : (
              <span className="admin-subtext">아직 없음</span>
            )}
          </div>
        </div>
        <label>
          <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>메모</span>
          <textarea value={draft.note} onChange={(e) => updateDraft(row.id, { note: e.target.value })} rows={2} />
        </label>
        <div className="am-actions" style={{ marginTop: -4, marginBottom: 16 }}>
          <button
            type="button"
            className="am-btn am-btn--primary"
            onClick={() => handleSaveApp(row.id)}
            disabled={savingKey === `app-${row.id}`}
          >
            {savingKey === `app-${row.id}` ? '저장 중...' : '상태·버전 저장'}
          </button>
        </div>

        <div style={{ fontSize: 13, marginBottom: 4 }}>
          Firebase 키:{' '}
          {row.firebaseKeyName ? (
            <strong title={row.firebaseProjectId ? `프로젝트: ${row.firebaseProjectId}` : undefined}>
              {row.firebaseKeyName}
              {row.firebaseProjectId ? ` (${row.firebaseProjectId})` : ''}
            </strong>
          ) : (
            <span className="admin-subtext">미등록</span>
          )}
        </div>
        <div className="am-field-row">
          <input
            type="file"
            accept="application/json,.json"
            disabled={savingKey === `firebase-${row.id}`}
            onChange={(e) => {
              handleUploadFirebaseKey(row.id, e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          {row.firebaseKeyName && (
            <button
              type="button"
              className="am-btn am-btn--danger"
              onClick={() => handleDeleteFirebaseKey(row.id)}
              disabled={savingKey === `firebase-${row.id}`}
            >
              키 삭제
            </button>
          )}
        </div>

        <div style={{ fontSize: 13, marginBottom: 4 }}>
          개인정보처리방침:{' '}
          {row.privacyPolicyName ? (
            <>
              <strong>{row.privacyPolicyName}</strong>{' '}
              <a href={`${MainURL}/api/privacy-policy/${authChurch}/${appType}`} target="_blank" rel="noreferrer">
                공개 URL 열기
              </a>
            </>
          ) : (
            <span className="admin-subtext">미등록</span>
          )}
        </div>
        <div className="am-field-row" style={{ marginBottom: 0 }}>
          <input
            type="file"
            accept="text/html,.html,.htm"
            disabled={savingKey === `policy-${row.id}`}
            onChange={(e) => {
              handleUploadPrivacyPolicy(row.id, e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          {row.privacyPolicyName && (
            <button
              type="button"
              className="am-btn am-btn--danger"
              onClick={() => handleDeletePrivacyPolicy(row.id)}
              disabled={savingKey === `policy-${row.id}`}
            >
              삭제
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderAppCell = (row: AppRow) => {
    if (!row) {
      return <span className="admin-status-tag">미생성</span>;
    }
    return (
      <div className="app-management-cell">
        <span className={`admin-status-tag${STATUS_TAG_MODIFIER[row.status]}`}>{STATUS_LABEL[row.status]}</span>
        {(row.buildVersion || row.minVersion) && (
          <div className="admin-subtext">
            {row.buildVersion ? `최신 ${row.buildVersion}` : ''}
            {row.buildVersion && row.minVersion ? ' · ' : ''}
            {row.minVersion ? `최소 ${row.minVersion}` : ''}
          </div>
        )}
        {row.lastReportedVersion && <div className="admin-subtext">사용 중 {row.lastReportedVersion}</div>}
        <div className="app-management-cell__badges">
          <span className={`app-management-cell__badge${row.firebaseKeyName ? ' app-management-cell__badge--ok' : ''}`}>
            Firebase {row.firebaseKeyName ? '등록' : '미등록'}
          </span>
          <span className={`app-management-cell__badge${row.privacyPolicyName ? ' app-management-cell__badge--ok' : ''}`}>
            처리방침 {row.privacyPolicyName ? '등록' : '미등록'}
          </span>
        </div>
      </div>
    );
  };

  // 선택 앱 칸: 사용 중이면 상태 + "사용 중지", 중지/미사용이면 "사용 시작"(주일학교는 이때 기본 정보를 자동으로 만든다).
  const renderOptionalCell = (church: ChurchAppRow, appType: OptionalAppType) => {
    const row = church.apps[appType];
    const busy = savingKey === `optional-${appType}-${church.authChurch}`;
    if (row && isEnabledRow(row)) {
      return (
        <div>
          {renderAppCell(row)}
          <button
            type="button"
            className="admin-btn delete-btn"
            style={{ marginTop: 6 }}
            onClick={() => handleToggleOptional(church.authChurch, church.churchName, appType, false)}
            disabled={busy}
          >
            사용 중지
          </button>
        </div>
      );
    }
    return (
      <div>
        <span className={`admin-status-tag${row ? ' admin-status-tag--danger' : ''}`}>{row ? '사용 중지됨' : '미사용'}</span>
        <div style={{ marginTop: 6 }}>
          <button
            type="button"
            className="admin-btn edit-btn"
            onClick={() => handleToggleOptional(church.authChurch, church.churchName, appType, true)}
            disabled={busy}
          >
            {row ? '다시 사용' : '사용 시작'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">앱관리</h2>
          <p className="admin-panel-desc">
            교회(authChurch)별 앱 연동 상태를 관리합니다. 결제로 등록된 교회는 사용자앱과 신청서에서 고른 앱이 자동으로
            켜지고, 직접(수동) 등록한 교회는 앱이 하나도 없이 시작하니 앱마다 &quot;사용 시작&quot;을 눌러 주세요(웹 교회앱관리 ·
            교적관리는 기본 제공). 관리자앱 · 교적부앱 · 재정앱 · 주일학교앱은 <strong>선택 서비스</strong>라 &quot;사용 중지&quot;도 할 수 있습니다. 주일학교앱도 &quot;사용 시작&quot;하면 기본 부서 · 반이
            자동으로 만들어지고, 네 앱 모두 교회 등록 때 만든 교회 관리자 계정으로 로그인합니다. 사용을 중지해도 데이터 · 설정은 지워지지 않고, 그 교회의 앱 · 웹 접속만
            막힙니다. 각 교회의 &quot;수정&quot; 버튼에서 앱별 빌드 상태 · 버전 · 메모와 Firebase 서비스 계정
            키(JSON) · 개인정보처리방침(HTML)을 등록합니다. 앱은 교회마다 authChurch를 하드코딩해 별도로 빌드하는
            구조라 빌드 진행 상태는 직접 갱신해 주세요.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="admin-refresh-btn" onClick={openCreateModal}>
            + 교회 수동 등록
          </button>
          <button type="button" className="admin-refresh-btn" onClick={fetchItems} disabled={loading}>
            {loading ? '불러오는 중...' : '새로고침'}
          </button>
        </div>
      </div>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>교회 (authChurch)</th>
              <th>area</th>
              <th>담당자</th>
              <th>사용자앱</th>
              <th>관리자앱 (선택)</th>
              <th>교적부앱 (선택)</th>
              <th>재정앱 (선택)</th>
              <th>주일학교앱 (선택)</th>
              <th>발급일</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={10}>등록된 교회가 아직 없습니다.</td>
              </tr>
            ) : (
              items.map((church) => (
                <tr key={church.authChurch}>
                  <td>
                    <div>{church.churchName}</div>
                    <div className="admin-subtext">{church.authChurch}</div>
                    {church.credentialStatus && (
                      <div style={{ marginTop: 4 }}>
                        <span className={`admin-status-tag${CREDENTIAL_TAG_MODIFIER[church.credentialStatus]}`}>
                          {church.credentialStatus === 'pending' && church.finalPaid
                            ? '로그인 정보 전달 필요'
                            : CREDENTIAL_LABEL[church.credentialStatus]}
                        </span>
                      </div>
                    )}
                  </td>
                  <td>{church.area ?? 1}</td>
                  <td>
                    <div>{church.contactName || '-'}</div>
                    <div className="admin-subtext">{church.contactEmail || '-'}</div>
                  </td>
                  <td>{church.apps.user ? renderAppCell(church.apps.user) : renderOptionalCell(church, 'user')}</td>
                  <td>{renderOptionalCell(church, 'admin')}</td>
                  <td>{renderOptionalCell(church, 'roll')}</td>
                  <td>{renderOptionalCell(church, 'finance')}</td>
                  <td>{renderOptionalCell(church, 'sundayschool')}</td>
                  <td>{String(church.createdAt).slice(0, 10)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="admin-btn edit-btn"
                        onClick={() => openAppEditModal(church.authChurch, church.churchName)}
                      >
                        수정
                      </button>
                      <button
                        type="button"
                        className="admin-btn delete-btn"
                        onClick={() => openDeleteModal(church.authChurch, church.churchName)}
                        disabled={savingKey === `delete-${church.authChurch}`}
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <div role="dialog" aria-modal="true" className="app-management-modal-overlay" onClick={closeCreateModal}>
          <div className="app-management-modal" onClick={(e) => e.stopPropagation()}>
            <h3>교회 수동 등록</h3>
            <p style={{ color: '#666', fontSize: 14 }}>
              온라인 결제 없이 최종관리자가 직접 교회를 등록합니다. 여기서 입력한 로그인 아이디(이메일) ·
              비밀번호로 churchAdmin.admins에 최종관리자(super) 계정이 그대로 생성됩니다(자동 발급 없음).
            </p>
            <label>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
                authChurch(교회앱 ID) * — 사용자앱 번들 ID를 그대로 사용하는 것을 권장 (예: com.newchurch)
              </span>
              <input
                type="text"
                value={createForm.authChurch}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, authChurch: e.target.value }))}
                placeholder="com.newchurch"
              />
            </label>
            <label>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
                area — 이 교회 데이터가 저장될 곳. 등록 후에는 바꿀 수 없고, 이 교회 앱들의 APP_AREA도 같은 값으로 빌드해야 합니다.
              </span>
              <select
                value={createForm.area}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, area: e.target.value }))}
              >
                <option value="">기본 area{areaOptions.find((a) => a.isDefault) ? ` (area ${areaOptions.find((a) => a.isDefault)?.area})` : ''}</option>
                {areaOptions.map((a) => (
                  <option key={a.area} value={String(a.area)}>
                    area {a.area}
                    {a.name && a.name !== `area ${a.area}` ? ` · ${a.name}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>교회명 *</span>
              <input
                type="text"
                value={createForm.churchName}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, churchName: e.target.value }))}
              />
            </label>
            <label>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>로그인 아이디 (이메일) *</span>
              <input
                type="email"
                value={createForm.userAccount}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, userAccount: e.target.value }))}
                placeholder="example@email.com"
                autoComplete="off"
              />
            </label>
            <label>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>비밀번호 *</span>
              <input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, password: e.target.value }))}
                autoComplete="new-password"
              />
            </label>
            <p style={{ fontSize: 13, color: '#666', margin: 0 }}>
              위 로그인 계정이 모든 앱 · 웹 관리 페이지의 교회 관리자 계정이 됩니다. 앱(사용자앱 포함)은 등록 후 목록에서 &quot;사용 시작&quot;을 눌러야 켜집니다.
            </p>
            <label>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>담당자명</span>
              <input
                type="text"
                value={createForm.contactName}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, contactName: e.target.value }))}
              />
            </label>
            <label>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>이메일</span>
              <input
                type="email"
                value={createForm.contactEmail}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, contactEmail: e.target.value }))}
              />
            </label>
            <label>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>연락처</span>
              <input
                type="text"
                value={createForm.contactPhone}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, contactPhone: e.target.value }))}
              />
            </label>
            {createError && <p className="admin-error-text">{createError}</p>}
            <div className="am-actions">
              <button type="button" className="am-btn am-btn--secondary" onClick={closeCreateModal}>
                취소
              </button>
              <button type="button" className="am-btn am-btn--primary" onClick={submitCreate} disabled={createSubmitting}>
                {createSubmitting ? '등록 중...' : '등록'}
              </button>
            </div>
          </div>
        </div>
      )}

      {appEditTarget != null && (() => {
        const church = items.find((c) => c.authChurch === appEditTarget.authChurch);
        if (!church) {
          // fetchItems가 갱신되는 사이(예: 방금 삭제된 authChurch) 잠깐 나타날 수 있는 상태 - 안내만 하고 닫게 한다.
          return (
            <div role="dialog" aria-modal="true" className="app-management-modal-overlay" onClick={closeAppEditModal}>
              <div className="app-management-modal" onClick={(e) => e.stopPropagation()}>
                <p className="admin-panel-desc">이 교회 정보를 더 이상 찾을 수 없습니다.</p>
                <div className="am-actions">
                  <button type="button" className="am-btn am-btn--secondary" onClick={closeAppEditModal}>
                    닫기
                  </button>
                </div>
              </div>
            </div>
          );
        }
        return (
          <div role="dialog" aria-modal="true" className="app-management-modal-overlay" onClick={closeAppEditModal}>
            <div className="app-management-modal app-management-modal--wide" onClick={(e) => e.stopPropagation()}>
              <h3>{church.churchName} — 앱 상세 수정</h3>
              <p style={{ color: '#666', fontSize: 13 }}>{church.authChurch}</p>
              {renderCredentialSection(church)}

              <div className="am-sections">
                {(['user', 'admin', 'roll', 'finance', 'sundayschool'] as const).map((appType) => {
                  const row = church.apps[appType];
                  const optional = appType !== 'user';
                  return (
                    <div className="am-section" key={appType}>
                      <h4>
                        {APP_TYPE_LABEL[appType]}
                        {optional ? ' (선택)' : ''}
                      </h4>
                      {row && optional && !isEnabledRow(row) && (
                        <p className="admin-error-text" style={{ marginTop: 0 }}>
                          사용 중지된 앱입니다. 설정은 수정할 수 있지만 교회는 접속할 수 없습니다.
                        </p>
                      )}
                      {row ? (
                        renderAppEditSection(row, church.authChurch, appType)
                      ) : (
                        <p className="admin-panel-desc">
                          사용하지 않는 앱입니다. 목록의 &quot;사용 시작&quot; 버튼으로 먼저 켜 주세요.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="am-actions">
                <button type="button" className="am-btn am-btn--secondary" onClick={closeAppEditModal}>
                  닫기
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {deleteTarget != null && (
        <div role="dialog" aria-modal="true" className="app-management-modal-overlay" onClick={closeDeleteModal}>
          <div className="app-management-modal" onClick={(e) => e.stopPropagation()}>
            <h3>교회 삭제</h3>
            <p style={{ color: '#666', fontSize: 14, userSelect: 'text' }}>
              {deleteTarget.churchName}({deleteTarget.authChurch}) 교회를 삭제하면 예배/설교/소식/교적/출석/재정/주일학교
              등 이 교회의 모든 데이터가 6개 DB에서 영구 삭제되며 되돌릴 수 없습니다.
            </p>
            <p style={{ fontSize: 14, userSelect: 'text' }}>
              계속하려면 authChurch 값을 정확히 입력하세요: <strong style={{ userSelect: 'all' }}>{deleteTarget.authChurch}</strong>
            </p>
            <label>
              <input
                type="text"
                value={deleteTyped}
                onChange={(e) => setDeleteTyped(e.target.value)}
                placeholder={deleteTarget.authChurch}
                autoComplete="off"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleDelete();
                }}
              />
            </label>
            {deleteTyped !== '' && deleteTyped !== deleteTarget.authChurch && (
              <p className="admin-error-text">입력한 authChurch가 일치하지 않습니다.</p>
            )}
            <div className="am-actions">
              <button type="button" className="am-btn am-btn--secondary" onClick={closeDeleteModal}>
                취소
              </button>
              <button
                type="button"
                className="am-btn am-btn--danger"
                onClick={handleDelete}
                disabled={deleteTyped !== deleteTarget.authChurch}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
